import { memo, useMemo, useRef, useState } from 'react';
import { useImmer } from 'use-immer';
import {
  faEnvelope,
  faSearch,
  faUser,
  faUserGear,
} from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope as faEnvelopeThin } from '@fortawesome/pro-thin-svg-icons';
import clsx from 'clsx';

import { AdminSection } from '@typings';
import { toast } from '@features';
import { getUsers, setSystemUserRole } from '@services';
import { capitalize, toastifyResponseError } from '@utils';

import {
  Button,
  ChaseSpinner,
  Field,
  HighlightExcerpt,
  Icon,
  Modal,
  ModalRef,
  Render,
} from '@components';
import { EmptyContent } from '@components/Ui';

type Props = {
  excludedUsers?: AdminSection.User[];
  onInvite: (user: AdminSection.User) => void;
};

export const SetRoleUserModal = memo(({ excludedUsers, onInvite }: Props) => {
  const modalRef = useRef<ModalRef>(null);

  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [selectedUser, setSelectedUser] = useState<AdminSection.User | null>(
    null,
  );

  const [filters, setFilters] = useImmer({
    search: '',
    role: AdminSection.Role.User,
  });

  const [users, setUsers] = useState<AdminSection.User[]>([]);

  const roleOptions = useMemo(
    () =>
      Object.values(AdminSection.Role).map((role) => ({
        value: role,
        text: capitalize(role),
      })),
    [],
  );

  const filteredUsers = useMemo(() => {
    const search = filters.search.toLocaleLowerCase();

    return users.filter(({ name, email }) =>
      [name, email].some((field) => field.toLocaleLowerCase().includes(search)),
    );
  }, [filters.search, users]);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => {
      prev.search = event.target.value;
    });
    setSelectedUser(null);
  };

  const handleGetUsers = async () => {
    try {
      setLoadingUsers(true);
      setSelectedUser(null);

      setFilters({
        search: '',
        role: AdminSection.Role.User,
      });

      const allUsers = await getUsers();

      if (excludedUsers) {
        setUsers(
          allUsers.filter(
            ({ email: userEmail }) =>
              !excludedUsers.some(
                ({ email: excludedEmail }) => excludedEmail === userEmail,
              ),
          ),
        );
      } else {
        setUsers(allUsers);
      }
    } catch (error) {
      toast.error('Could not fetch user list');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserInvite = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      setLoading(true);

      await setSystemUserRole({
        role: filters.role,
        name: selectedUser.name,
      });

      onInvite?.({
        ...selectedUser,
        role: filters.role,
      });

      modalRef.current?.close();
      toast.success(`User ${selectedUser.email} invited`);
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      setLoading(false);
    }
  };

  const renderUsers = () => {
    if (filters.search.length < 2) {
      return (
        <EmptyContent
          className="py-6"
          icon={faEnvelopeThin}
          title="Invite a user via email or username"
          text="Type at least 2 characters"
        />
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <EmptyContent className="py-6" icon={faSearch} title="No users found" />
      );
    }

    return filteredUsers.map((user) => {
      const isSelected = user.email === selectedUser?.email;

      return (
        <Button
          key={user.name}
          theme
          className={clsx(
            'flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background',
            { 'bg-background': isSelected },
          )}
          onClick={() => setSelectedUser(user)}
        >
          <Icon
            icon={faUser}
            className={clsx(
              'h-10 w-10 overflow-hidden rounded-full bg-stroke text-neural-04',
              { 'bg-primary-light text-white': isSelected },
            )}
          />

          <div className="text-left">
            <HighlightExcerpt search={filters.search} text={user.name} />
            <p className="text-caption text-neural-04 transition-colors">
              <HighlightExcerpt search={filters.search} text={user.email} />
            </p>
          </div>
        </Button>
      );
    });
  };

  const content = (
    <Modal.Content title="Set a role" className="w-[578px]">
      <Render if={loadingUsers}>
        <div className="flex w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-8 w-8" />
        </div>
      </Render>

      <Render if={!loadingUsers}>
        <div className="flex gap-4">
          <Field.Input
            value={filters.search}
            containerClassName="flex-1"
            label="Username / Email"
            note="Search by name or email"
            onChange={handleUsernameChange}
          />

          <Field.Select
            label="Role"
            options={roleOptions}
            containerClassName="basis-[140px]"
            onChange={(event) =>
              setFilters((prev) => {
                prev.role = event.target.value as AdminSection.Role;
              })
            }
          />
        </div>

        <div className="mt-6 flex h-[240px] flex-col gap-4 overflow-auto">
          {renderUsers()}
        </div>
      </Render>

      <Modal.Footer>
        <Button variant="secondary" onClick={modalRef.current?.close}>
          Cancel
        </Button>
        <Button
          icon={faEnvelope}
          disabled={!selectedUser}
          loading={loading || loadingUsers}
          className="pl-6 pr-8"
          onClick={handleUserInvite}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );

  return (
    <Modal ref={modalRef} content={content} onOpen={handleGetUsers}>
      <Button
        variant="rebecca"
        className="gap-3 capitalize"
        onClick={handleGetUsers}
      >
        <Icon icon={faUserGear} />
        Set role
      </Button>
    </Modal>
  );
});

SetRoleUserModal.displayName = 'SetRoleUserModal';
